/** Master list of a notification's delivery methods: icon + label, enabled
 *  toggle, rendering chip (S/C), theme caption, a validation status dot fed by
 *  the page-held validation state, drag-reorder (order = list position on
 *  the wire; `sequence` is resequenced locally for display), an "Add method"
 *  menu (appends a draft row — created on save) and per-row delete (removes
 *  the row from the draft — the save's full-list reconcile deletes it).
 */
import AddIcon from '@mui/icons-material/Add';
import BugReportIcon from '@mui/icons-material/BugReport';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EmailIcon from '@mui/icons-material/Email';
import HouseIcon from '@mui/icons-material/House';
import HttpIcon from '@mui/icons-material/Http';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SmsIcon from '@mui/icons-material/Sms';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState, type ReactElement } from 'react';

import type { NotificationDeliveryMethod, ThemeBrief } from '@/api/notifications';
import { DELIVERY_METHODS, DELIVERY_METHOD_LABELS, RENDER_STANDARD } from '@/api/notifications';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { deliveryMethodActions } from '../notificationLinks';
import { channelSpec } from './channelFields';

export type DmValidationStatus = 'none' | 'ok' | 'stale' | 'invalid';

const METHOD_ICONS: Record<number, ReactElement> = {
  [DELIVERY_METHODS.EMAIL]: <EmailIcon fontSize="small" />,
  [DELIVERY_METHODS.SMS]: <SmsIcon fontSize="small" />,
  [DELIVERY_METHODS.CHAT]: <ChatBubbleOutlineIcon fontSize="small" />,
  [DELIVERY_METHODS.PUSH_NOTIFICATION]: <NotificationsIcon fontSize="small" />,
  [DELIVERY_METHODS.SENTRY]: <BugReportIcon fontSize="small" />,
  [DELIVERY_METHODS.SNS_TOPIC]: <CloudQueueIcon fontSize="small" />,
  [DELIVERY_METHODS.HTTP_REQUEST]: <HttpIcon fontSize="small" />,
  [DELIVERY_METHODS.WHATSAPP]: <WhatsAppIcon fontSize="small" />,
  [DELIVERY_METHODS.AIRBNB]: <HouseIcon fontSize="small" />,
};

const STATUS_META: Record<DmValidationStatus, { color: string; title: string }> = {
  none: { color: 'success.main', title: 'No template issues detected' },
  ok: { color: 'success.main', title: 'Templates validated' },
  stale: { color: 'warning.main', title: 'Edited since the last validation' },
  invalid: { color: 'error.main', title: 'Template validation failed' },
};

export function methodLabel(method: number): string {
  return DELIVERY_METHOD_LABELS[method] ?? `Method ${method}`;
}

interface Props {
  items: NotificationDeliveryMethod[];
  /** Theme briefs from the detail refs, keyed by id (string). */
  themes: Record<string, ThemeBrief>;
  selectedId: number | null;
  statusById: Record<number, DmValidationStatus>;
  canManage: boolean;
  onSelect: (id: number) => void;
  onToggleEnabled: (id: number, enabled: boolean) => void;
  /** Receives the full re-ordered list with `sequence` resequenced. */
  onReorder: (ordered: NotificationDeliveryMethod[]) => void;
  /** Append a new draft method of this channel (created on save). */
  onAdd: (method: number) => void;
  /** Drop the row from the draft (deleted on save — full-list reconcile). */
  onRemove: (dmId: number) => void;
}

/** Channel codes in display order for the "Add method" menu. */
const ADD_MENU_METHODS: number[] = Object.keys(DELIVERY_METHOD_LABELS)
  .map(Number)
  .sort((a, b) => a - b);

export function DeliveryMethodList({
  items,
  themes,
  selectedId,
  statusById,
  canManage,
  onSelect,
  onToggleEnabled,
  onReorder,
  onAdd,
  onRemove,
}: Props) {
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  const [pendingRemove, setPendingRemove] = useState<NotificationDeliveryMethod | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((d) => d.id === Number(active.id));
    const newIndex = items.findIndex((d) => d.id === Number(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(items, oldIndex, newIndex).map((dm, idx) => ({ ...dm, sequence: idx })));
  };

  return (
    <Box>
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
          No delivery methods yet.
        </Typography>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map((d) => d.id)} strategy={verticalListSortingStrategy}>
            <List dense disablePadding>
              {items.map((dm) => (
                <SortableDmRow
                  key={dm.id}
                  dm={dm}
                  themeCodename={dm.theme != null ? (themes[String(dm.theme)]?.codename ?? `#${dm.theme}`) : null}
                  selected={dm.id === selectedId}
                  status={statusById[dm.id] ?? 'none'}
                  canManage={canManage}
                  onSelect={() => onSelect(dm.id)}
                  onToggleEnabled={(enabled) => onToggleEnabled(dm.id, enabled)}
                  onRemove={() => setPendingRemove(dm)}
                />
              ))}
            </List>
          </SortableContext>
        </DndContext>
      )}

      {canManage && (
        <>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={(e) => setAddAnchor(e.currentTarget)}
            sx={{ mt: 0.5 }}
          >
            Add method
          </Button>
          <Menu anchorEl={addAnchor} open={Boolean(addAnchor)} onClose={() => setAddAnchor(null)}>
            {ADD_MENU_METHODS.map((method) => (
              <MenuItem
                key={method}
                onClick={() => {
                  setAddAnchor(null);
                  onAdd(method);
                }}
              >
                <ListItemIcon sx={{ minWidth: 30 }}>
                  {METHOD_ICONS[method] ?? <NotificationsIcon fontSize="small" />}
                </ListItemIcon>
                <ListItemText>{methodLabel(method)}</ListItemText>
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      <ConfirmDialog
        open={pendingRemove !== null}
        title="Remove this delivery method?"
        message={`Removing a delivery method deletes it on save. "${
          pendingRemove ? methodLabel(pendingRemove.method) : ''
        }" will be deleted when you save the notification; discard your changes to keep it.`}
        confirmLabel="Remove method"
        destructive
        onConfirm={() => {
          if (pendingRemove) onRemove(pendingRemove.id);
          setPendingRemove(null);
        }}
        onCancel={() => setPendingRemove(null)}
      />
    </Box>
  );
}

interface RowProps {
  dm: NotificationDeliveryMethod;
  themeCodename: string | null;
  selected: boolean;
  status: DmValidationStatus;
  canManage: boolean;
  onSelect: () => void;
  onToggleEnabled: (enabled: boolean) => void;
  onRemove: () => void;
}

function SortableDmRow({
  dm,
  themeCodename,
  selected,
  status,
  canManage,
  onSelect,
  onToggleEnabled,
  onRemove,
}: RowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: dm.id,
    disabled: !canManage,
  });
  const spec = channelSpec(dm.method);
  const renderingType = spec.renderingTypeField ? (dm[spec.renderingTypeField] as number) : null;
  const statusMeta = STATUS_META[status];

  return (
    <ListItemButton
      ref={setNodeRef}
      selected={selected}
      onClick={onSelect}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        borderRadius: 1,
        gap: 0.5,
      }}
    >
      <Box
        component="span"
        aria-label="Drag to reorder"
        sx={{
          display: 'inline-flex',
          cursor: canManage ? 'grab' : 'default',
          color: 'text.disabled',
          touchAction: 'none',
        }}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>
      <ListItemIcon sx={{ minWidth: 30 }}>{METHOD_ICONS[dm.method] ?? <NotificationsIcon fontSize="small" />}</ListItemIcon>
      <ListItemText
        primary={
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 400 }}>
              {methodLabel(dm.method)}
            </Typography>
            {dm.id < 0 && (
              <Tooltip title="Added in this session — created when you save.">
                <Chip
                  size="small"
                  color="info"
                  variant="outlined"
                  label="new"
                  sx={{ height: 18, '& .MuiChip-label': { px: 0.75 } }}
                />
              </Tooltip>
            )}
            {spec.composition && renderingType != null && (
              <Tooltip title={renderingType === RENDER_STANDARD ? 'Standard template' : 'Custom template'}>
                <Chip
                  size="small"
                  variant="outlined"
                  label={renderingType === RENDER_STANDARD ? 'S' : 'C'}
                  sx={{ height: 18, '& .MuiChip-label': { px: 0.75 } }}
                />
              </Tooltip>
            )}
            {spec.templateFields.length > 0 && (
              <Tooltip title={statusMeta.title}>
                <Box
                  component="span"
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: statusMeta.color,
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            )}
          </Stack>
        }
        secondary={
          // The theme is the one thing on this row with a page of its own —
          // `dm.theme` IS the Notification_Theme id, so the row's own id never
          // belongs in that link.
          themeCodename ? (
            <ValueActions actions={deliveryMethodActions(dm, themeCodename)}>
              theme: {themeCodename}
            </ValueActions>
          ) : undefined
        }
        secondaryTypographyProps={{ variant: 'caption', noWrap: true, component: 'div' }}
      />
      <Switch
        size="small"
        checked={dm.enabled}
        disabled={!canManage}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onToggleEnabled(e.target.checked)}
        inputProps={{ 'aria-label': `${methodLabel(dm.method)} enabled` }}
      />
      {canManage && (
        <Tooltip title="Remove (deleted on save)">
          <IconButton
            size="small"
            aria-label={`Remove ${methodLabel(dm.method)} delivery method`}
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </ListItemButton>
  );
}
